import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { HardDrive } from "lucide-react";
import { translations } from "../../../../utils/language";

const BYTES_IN_MB = 1_000_000;

const formatMb = (bytes) => {
	const mb = Math.max(0, Number(bytes) || 0) / BYTES_IN_MB;
	if (mb >= 1000) return `${(mb / 1000).toFixed(mb >= 10000 ? 0 : 1)} GB`;
	return `${mb.toFixed(mb < 10 ? 2 : 1)} MB`;
};

const getLimitLabel = (limitBytes) => {
	const limit = String(limitBytes ?? "");
	if (limit === "10000000000") return "10 GB";
	if (limit === "20000000000") return "20 GB";
	if (limit === "1000000000") return "1 GB";
	return formatMb(limitBytes);
};

const ArmazenamentoPage = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const settings = useSelector((state) => state.reducerSettings);
	const language = useSelector((state) => state.reducerLanguage);

	useEffect(() => {
		dispatch({ type: "SAGA_LIST_SETTINGS" });
	}, [dispatch]);

	const { usedBytes, limitBytes, percent, barColor } = useMemo(() => {
		const used = Math.max(0, Number(settings?.data?.data?.storage) || 0);
		const limit = Math.max(0, Number(settings?.data?.data?.limit) || 0);
		const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
		let color = "#21D487";
		if (pct > 60 && pct < 90) color = "#FFC400";
		if (pct >= 90) color = "#F13F6C";
		return {
			usedBytes: used,
			limitBytes: limit,
			percent: pct,
			barColor: color,
		};
	}, [settings]);

	const loading = settings.loading;

	return (
		<div className="mx-auto w-full max-w-2xl px-4 py-6 md:px-6">
			<div className="mb-6">
				<h1 className="text-xl font-semibold tracking-tight text-slate-900">
					{translations["Armazenamento"]?.[language.data] || "Armazenamento"}
				</h1>
				<p className="mt-1 text-sm text-gray-500">
					{translations["armazenamento-desc"]?.[language.data] ||
						"Acompanhe o uso do espaço da sua conta."}
				</p>
			</div>

			<div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
				<div className="flex items-start gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3.5">
					<div className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700">
						<HardDrive size={16} strokeWidth={1.5} />
					</div>
					<div className="min-w-0">
						<p className="text-sm font-semibold text-slate-800">
							{translations["Armazenamento"]?.[language.data] || "Armazenamento"}
						</p>
						<p className="text-xs text-gray-500">
							{translations["armazenamento-desc"]?.[language.data] ||
								"Acompanhe o uso do espaço da sua conta."}
						</p>
					</div>
				</div>

				<div className="flex flex-col gap-4 px-4 py-4">
					<div className="flex items-end justify-between gap-3">
						<div>
							<p className="text-xs font-medium uppercase tracking-wide text-gray-500">
								{translations["armazenamento-usado"]?.[language.data] || "Usado"}
							</p>
							<p className="mt-0.5 text-lg font-semibold tabular-nums text-slate-900">
								{loading ? "—" : formatMb(usedBytes)}
							</p>
						</div>
						<div className="text-right">
							<p className="text-xs font-medium uppercase tracking-wide text-gray-500">
								{translations["armazenamento-limite"]?.[language.data] || "Limite"}
							</p>
							<p className="mt-0.5 text-sm font-medium tabular-nums text-slate-700">
								{loading ? "—" : getLimitLabel(limitBytes)}
							</p>
						</div>
					</div>

					<div className="flex flex-col gap-2">
						<div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
							<div
								className="h-full rounded-full transition-all duration-300"
								style={{
									width: loading ? "0%" : `${percent}%`,
									backgroundColor: barColor,
								}}
							/>
						</div>
						<div className="flex items-center justify-between text-xs text-gray-500">
							<span>
								{loading
									? "…"
									: `${percent.toFixed(1)}% ${
											translations["armazenamento-utilizado"]?.[language.data] ||
											"utilizado"
									  }`}
							</span>
							<span className="tabular-nums">
								{loading
									? "—"
									: `${formatMb(usedBytes)} / ${getLimitLabel(limitBytes)}`}
							</span>
						</div>
					</div>

					<button
						type="button"
						onClick={() => history.push("/pagamentos")}
						className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 transition-colors hover:bg-gray-50"
					>
						{translations["header-comprar"]?.[language.data] || "Comprar mais espaço"}
					</button>
				</div>
			</div>
		</div>
	);
};

export default ArmazenamentoPage;
